import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Database,
  Bell,
  Users,
  Building2,
  Mail,
  Inbox,
  ScrollText,
  LogOut,
  Sun,
  Moon,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import Logo from "./Logo";
import PaperOverlay from "./PaperOverlay";
import LangSwitcher from "./LangSwitcher";
import { useT } from "../lib/i18n";

const NAV = [
  { to: "/dashboards", label: "nav.dashboards", icon: LayoutDashboard, role: "any" },
  { to: "/datasources", label: "nav.datasources", icon: Database, role: "admin" },
  { to: "/alarms", label: "nav.alarms", icon: Bell, role: "admin" },
  { to: "/users", label: "nav.users", icon: Users, role: "admin" },
  { to: "/companies", label: "nav.companies", icon: Building2, role: "admin" },
  { to: "/smtp", label: "nav.smtp", icon: Mail, role: "admin" },
  { to: "/contact-messages", label: "nav.contact_messages", icon: Inbox, role: "admin" },
  { to: "/audit", label: "nav.audit", icon: ScrollText, role: "admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const t = useT();
  const navigate = useNavigate();
  const nav = NAV.filter((n) => n.role === "any" || user?.role === "admin");

  const initials = (user?.name || user?.email || "U")
    .split(/\s+|@/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 border-r"
        style={{
          backgroundColor: "hsl(var(--sidebar-bg))",
          color: "hsl(var(--sidebar-fg))",
          borderColor: "hsl(var(--sidebar-border))",
        }}
        data-testid="sidebar"
      >
        <div
          className="h-16 flex items-center gap-3 px-5 border-b"
          style={{ borderColor: "hsl(var(--sidebar-border))" }}
        >
          <Logo
            className="h-9 w-9 logo-on-dark"
            color="hsl(var(--sidebar-fg))"
            accent="hsl(var(--sidebar-active))"
          />
          <div className="leading-tight">
            <div className="brand-text font-bold text-[13px] tracking-[0.18em] uppercase">
              Olimpia
            </div>
            <div
              className="brand-text font-semibold text-[10px] tracking-[0.32em] uppercase"
              style={{ color: "hsl(var(--sidebar-muted))" }}
            >
              Sensor
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.replace("nav.", "")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-[hsl(var(--sidebar-muted))] hover:bg-white/5 hover:text-[hsl(var(--sidebar-fg))]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{t(label)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--sidebar-muted))]">
          v1.0 · {t("nav.tagline")}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 brand-paper relative">
        <PaperOverlay />        <header className="h-14 border-b border-border px-4 md:px-6 flex items-center justify-between bg-background/80 backdrop-blur sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <span className="kpi-label hidden sm:inline">{t("nav.workspace")}</span>
            <span className="font-medium text-sm truncate">
              {user?.role === "admin" ? t("nav.all_companies") : t("nav.my_company")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LangSwitcher />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-[11px] bg-primary text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-xs font-medium">{user?.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {user?.role}
                    </span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-xs text-muted-foreground">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" /> {t("auth.log_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 fade-in" data-testid="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
